// components/Search/GlobalSearch.jsx
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { debounce } from "lodash";
import { useLocation, useParams } from "react-router-dom";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  ClickAwayListener,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

// ─── Constants ────────────────────────────────────────────────────────────────

const BASE_URL = "https://maintenanceshift.co.za/api";

const TYPE_LABELS = {
  sites: "Site",
  plants: "Plant",
  assets: "Main Asset",
  "sub-assets": "Sub Asset",
  "panel-components": "Panel Component",
  instruments: "Instrument",
};

const SEARCH_FIELDS = {
  sites: "siteName",
  plants: "siteName",
  assets: "tagNumber",
  "sub-assets": "tagNumber",
  "panel-components": "tagNumber",
  instruments: "tagNumber",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getAuthHeaders = () => {
  const authTokens = localStorage.getItem("authTokens");
  if (authTokens) {
    try {
      const tokens = JSON.parse(authTokens);
      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tokens.access}`,
      };
    } catch {
      // malformed token — fall through
    }
  }
  return { "Content-Type": "application/json" };
};

const fetchJson = async (url, signal) => {
  const res = await fetch(url, { headers: getAuthHeaders(), signal });
  if (!res.ok) return null;
  return res.json();
};

// ─── GlobalSearch ─────────────────────────────────────────────────────────────
//
// Props
// ─────
// localItems        {Array}     Items visible on the current page.
// localSearchKeys   {string[]}  Object keys to search within localItems.
// onResultsChange   {Function}  (items, isActive, mode) => void
// onLoadingChange   {Function}  (isLoading: boolean) => void   ← NEW
//                               Called when global-search loading state changes.
//                               Parent uses this to show a spinner in the content
//                               area instead of in the search bar.
// globalScope       {string[]}  Optional override for which entity types to search.
// onQueryChange     {Function}  Optional. (query: string) => void - the trimmed
//                               "This page" search text, debounced 300ms ("" when
//                               cleared or in "All" mode), for pages that search
//                               their own lists on the server instead of relying
//                               on localItems matches.

const GlobalSearch = ({
  localItems = [],
  localSearchKeys = [
    "tagNumber",
    "componentName",
    "equipmentName",
    "siteName",
    "tierName",
    "description",
    "serialNumber",
  ],
  onResultsChange,
  onLoadingChange, // ← new prop
  globalScope,
  onQueryChange,
}) => {
  const [focused, setFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMode, setSearchMode] = useState("page");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const inputRef = useRef(null);
  const location = useLocation();
  const params = useParams();

  // ── All volatile values go into refs ─────────────────────────────────────
  const onResultsChangeRef = useRef(onResultsChange);
  useEffect(() => {
    onResultsChangeRef.current = onResultsChange;
  }, [onResultsChange]);

  const onLoadingChangeRef = useRef(onLoadingChange);
  useEffect(() => {
    onLoadingChangeRef.current = onLoadingChange;
  }, [onLoadingChange]);

  const onQueryChangeRef = useRef(onQueryChange);
  useEffect(() => {
    onQueryChangeRef.current = onQueryChange;
  }, [onQueryChange]);

  const paramsRef = useRef(params);
  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  const globalScopeRef = useRef(globalScope);
  const prevGlobalScopeJson = useRef(JSON.stringify(globalScope));
  useEffect(() => {
    const next = JSON.stringify(globalScope);
    if (next !== prevGlobalScopeJson.current) {
      prevGlobalScopeJson.current = next;
      globalScopeRef.current = globalScope;
    }
  }, [globalScope]);

  const searchScopeRef = useRef([]);
  useEffect(() => {
    const path = location.pathname;
    let scope = [];
    if (path === "/" || path === "/sites") {
      scope = ["sites", "plants", "assets", "sub-assets", "panel-components"];
    } else if (path.match(/^\/sites\/\d+$/)) {
      scope = ["plants", "assets", "sub-assets", "panel-components"];
    } else if (path.match(/^\/sites\/\d+\/plants\/\d+$/)) {
      scope = ["assets", "sub-assets", "panel-components"];
    } else if (path.match(/^\/sites\/\d+\/plants\/\d+\/assets\/\d+$/)) {
      scope = ["sub-assets", "panel-components"];
    } else if (
      path.match(/^\/sites\/\d+\/plants\/\d+\/assets\/\d+\/sub-asset\/\d+/)
    ) {
      scope = ["panel-components"];
    }
    searchScopeRef.current = scope;
  }, [location.pathname]);
  // ─────────────────────────────────────────────────────────────────────────

  const showTabs = focused || searchQuery.trim().length > 0;

  // ── Local filtering ──────────────────────────────────────────────────────

  const localResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return localItems;
    return localItems.filter((item) =>
      localSearchKeys.some((key) => {
        const val = item[key];
        return typeof val === "string" && val.toLowerCase().includes(q);
      }),
    );
  }, [searchQuery, localItems, localSearchKeys]);

  // ── Endpoint builder — reads params from ref, truly stable ──────────────

  const getApiEndpoints = useCallback(() => {
    const p = paramsRef.current;
    const e = {
      sites: `${BASE_URL}/sites/`,
      plants: `${BASE_URL}/plants/`,
      assets: `${BASE_URL}/equipment/`,
      "sub-assets": `${BASE_URL}/tiers/`,
      "panel-components": `${BASE_URL}/panel-components/`,
      instruments: `${BASE_URL}/instruments/`,
    };
    if (p.siteId) {
      e.plants = `${BASE_URL}/plants/?site=${p.siteId}`;
      e.assets = `${BASE_URL}/equipment/?site=${p.siteId}`;
      e["sub-assets"] = `${BASE_URL}/tiers/?site=${p.siteId}`;
      e["panel-components"] = `${BASE_URL}/panel-components/?site=${p.siteId}`;
    }
    if (p.plantId) {
      e.assets = `${BASE_URL}/equipment/?plant=${p.plantId}`;
      e["sub-assets"] = `${BASE_URL}/tiers/?plant=${p.plantId}`;
      e["panel-components"] =
        `${BASE_URL}/panel-components/?plant=${p.plantId}`;
    }
    if (p.assetId) {
      e["sub-assets"] = `${BASE_URL}/tiers/?relatedTo=${p.assetId}`;
      e["panel-components"] =
        `${BASE_URL}/panel-components/?asset=${p.assetId}`;
    }
    if (p.subAssetId) {
      e["panel-components"] =
        `${BASE_URL}/panel-components/?tier=${p.subAssetId}`;
      e["instruments"] = `${BASE_URL}/instruments/?tier=${p.subAssetId}`;
    }
    return e;
  }, []);

  // ── Debounce ─────────────────────────────────────────────────────────────

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetQuery = useCallback(
    debounce((q) => setDebouncedQuery(q), 300),
    [],
  );
  useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedReportPageQuery = useCallback(
    debounce((q) => onQueryChangeRef.current?.(q), 300),
    [],
  );
  useEffect(() => () => debouncedReportPageQuery.cancel(), [debouncedReportPageQuery]);

  // "This page" text for onQueryChange: typing is debounced, while clearing
  // (or leaving page mode) reports "" straight away.
  useEffect(() => {
    const q = searchMode === "page" ? searchQuery.trim() : "";
    if (q) {
      debouncedReportPageQuery(q);
    } else {
      debouncedReportPageQuery.cancel();
      onQueryChangeRef.current?.("");
    }
  }, [searchQuery, searchMode, debouncedReportPageQuery]);

  // ── Global search — stable, all volatile values via refs ─────────────────

  const performGlobalSearch = useCallback(
    async (query, signal) => {
      if (!query.trim() || query.trim().length < 2) {
        onResultsChangeRef.current?.([], false, "all");
        onLoadingChangeRef.current?.(false);
        return;
      }

      // Tell parent to show content-area spinner and clear stale results
      onLoadingChangeRef.current?.(true);
      onResultsChangeRef.current?.([], true, "all");

      try {
        const endpoints = getApiEndpoints();
        const scopeToUse = globalScopeRef.current || searchScopeRef.current;

        const promises = scopeToUse.map(async (scope) => {
          try {
            const base = endpoints[scope];
            if (!base) return [];
            const url = `${base}${base.includes("?") ? "&" : "?"}search=${encodeURIComponent(query)}`;
            const data = await fetchJson(url, signal);
            if (!data) return [];
            const items = Array.isArray(data) ? data : (data?.results ?? []);
            return items.map((item) => ({
              ...item,
              _type: scope,
              _typeLabel: TYPE_LABELS[scope] || scope,
              _displayName: item[SEARCH_FIELDS[scope]],
            }));
          } catch {
            return [];
          }
        });

        const flat = (await Promise.all(promises)).flat().slice(0, 60);
        onResultsChangeRef.current?.(flat, true, "all");
      } catch (e) {
        if (e?.name !== "AbortError") console.error("Global search error:", e);
      } finally {
        onLoadingChangeRef.current?.(false);
      }
    },
    [getApiEndpoints],
  );

  // ── Page mode effect ──────────────────────────────────────────────────────

  useEffect(() => {
    if (searchMode !== "page") return;
    const isActive = searchQuery.trim().length > 0;
    onResultsChangeRef.current?.(
      isActive ? localResults : [],
      isActive,
      "page",
    );
  }, [localResults, searchMode, searchQuery]);

  // ── All mode effect ───────────────────────────────────────────────────────

  useEffect(() => {
    if (searchMode !== "all") return;
    if (debouncedQuery.trim().length >= 2) {
      const controller = new AbortController();
      performGlobalSearch(debouncedQuery, controller.signal);
      return () => controller.abort();
    } else {
      onResultsChangeRef.current?.([], false, "all");
      onLoadingChangeRef.current?.(false);
    }
  }, [debouncedQuery, searchMode, performGlobalSearch]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleInputChange = (value) => {
    setSearchQuery(value);
    if (searchMode === "all") debouncedSetQuery(value);
  };

  const handleModeChange = (mode) => {
    if (mode === searchMode) return;
    setSearchMode(mode);
    if (mode === "page") {
      onLoadingChangeRef.current?.(false);
      const isActive = searchQuery.trim().length > 0;
      onResultsChangeRef.current?.(
        isActive ? localResults : [],
        isActive,
        "page",
      );
    } else {
      if (searchQuery.trim().length >= 2) debouncedSetQuery(searchQuery);
      else onResultsChangeRef.current?.([], false, "all");
    }
  };

  const handleClear = () => {
    setSearchQuery("");
    setDebouncedQuery("");
    setSearchMode("page");
    setFocused(false);
    onLoadingChangeRef.current?.(false);
    onResultsChangeRef.current?.([], false, "page");
  };

  const handleClickAway = () => {
    if (!searchQuery.trim()) setFocused(false);
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 400,
          minWidth: 220,
          mx: "auto",
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search…"
          value={searchQuery}
          inputRef={inputRef}
          onFocus={() => setFocused(true)}
          onChange={(e) => handleInputChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {/* Always show static search icon — loading moves to content area */}
                <SearchIcon sx={{ color: "primary.main", fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClear}
                  sx={{ color: "text.disabled", p: "2px" }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: { borderRadius: "8px" },
          }}
        />

        <Fade in={showTabs} timeout={160}>
          <Box
            sx={{
              position: "absolute",
              top: "calc(100% + 8px)",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              backgroundColor: "#fff",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "24px",
              px: 0.5,
              py: 0.35,
              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
              whiteSpace: "nowrap",
            }}
          >
            {[
              { key: "page", label: "This page" },
              { key: "all", label: "All" },
            ].map((tab) => {
              const active = searchMode === tab.key;
              return (
                <Box
                  key={tab.key}
                  onClick={() => handleModeChange(tab.key)}
                  sx={{
                    px: 1.5,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "20px",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.15s ease",
                    backgroundColor: active ? "primary.main" : "transparent",
                    color: active ? "#fff" : "text.secondary",
                    border: "1.5px solid",
                    borderColor: active ? "primary.main" : "#e0e0e0",
                    "&:hover": {
                      borderColor: "primary.main",
                      color: active ? "#fff" : "primary.main",
                    },
                  }}
                >
                  {tab.label}
                </Box>
              );
            })}
          </Box>
        </Fade>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
